<template>
  <ChartBlock class="demographics-content">
    <div class="actions-row">
      <FilterRow
        label="FILTERS"
        class="filter-row"
        :displayLimit="filtersLimit"
        @add="$emit('add', $event)"
        @complete="$emit('complete')"
      />
    </div>
    <ReportMessage
      v-if="showReportMessage"
      class="content"
      @click="$emit('openSlideOut')"
      :message="message"
    />
    <div v-else id="demographics-table">
      <Table
        v-if="hasValidDataToDisplay || localLoading"
        :data="tableData"
        :customColumns="tableColumns"
        :loading="localLoading"
        :loading-text="'loading...'"
        :fixed-header="true"
        :disableFooterItemsPerPage="true"
        :items-per-page="50"
        :initialSortKey="sortKey"
        :initialSortDesc="true"
      />
    </div>
  </ChartBlock>
</template>
<script>
import { mapState } from 'vuex';
import FilterRow from '../Common/FilterRow.vue';
import ChartBlock from '../Common/Charting/Shared/ChartBlock.vue';
import ReportMessage from '../Common/ReportMessage';
import Table from '../Common/Table.vue';
import {
  getReportFieldFromValue,
  getDemographicsFieldName,
} from '../../../services/dashboard/reportFieldService';

export default {
  props: {
    portfolioBrands: {
      type: Array,
    },
    selectedFilters: {
      type: Array,
    },
    isNewReport: {
      type: Boolean,
      default: false,
    },
    hasThresholdError: {
      type: Boolean,
      default: false,
    },
    hasDefaultErrorMessage: {
      type: Boolean,
      default: false,
    },
    hasNoResultsMessage: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
    },
    dimension: {
      type: String,
    },
    metrics: {
      type: Array,
    },
    summaryRow: {
      type: Object,
    },
  },
  data() {
    return {
      filtersLimit: 2,
      sortKey: '',
      localLoading: this.loading,
      reportMessages: {
        newReport: {
          title: 'Create a new report',
          description: [
            'To create a demographics report, select "Build a Report"',
            'to add dimensions, metrics, and filters.',
          ],
          isAlert: false,
          isNewReport: true,
        },
        thresholdError: {
          title: 'Not Enough Data',
          description: [
            'The number of page views doesn\'t meet MikMak\'s ' +
            'minimum threshold for statistical significance.',
            'Please modify your report filters or date range ' +
            'to view a larger data set.',
          ],
          tip: 'Tip: Run a report with All Segments instead.',
          action: {
            label: 'Run All Segments Report',
            function: this.runAllSegments,
          },
          isAlert: true,
          isNewReport: false,
        },
        defaultErrorMessage: {
          title: 'Oops! Something went wrong',
          description: [
            'Please build a new report. If the problem persists,',
            'please contact your MikMak representative.',
          ],
          isAlert: true,
          isNewReport: false,
        },
        noResultMessage: {
          title: 'No results found',
          description: [
            'Your report did not return any data results.',
            'Please update your report and try again.',
          ],
          isAlert: false,
          isNewReport: false,
          noResults: true,
        },
      },
    };
  },
  computed: {
    ...mapState({
      includeFullSegments: state => state.dashboard.reportRequest.demographicsIncludeFullSegments,
      allSegments: state => state.dashboard.availableReportFields.allSegmentsDimension,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    showReportMessage() {
      return !this.localLoading && this.hasMessage;
    },
    hasMessage() {
      return this.hasThresholdError ||
        this.hasDefaultErrorMessage ||
        this.hasNoResultsMessage ||
        this.isNewReport;
    },
    hasValidDataToDisplay() {
      return this.tableData
        && this.tableColumns
        && this.tableColumns.length > 0;
    },
    message() {
      if (this.hasThresholdError)    {
        if (this.dimension === this.allSegments.value) {
          return {
            ...this.reportMessages.thresholdError,
            tip: undefined,
            action: undefined,
          };
        }

        return this.reportMessages.thresholdError;
      }
      if (this.hasDefaultErrorMessage) return this.reportMessages.defaultErrorMessage;
      if (this.hasNoResultsMessage) return this.reportMessages.noResultMessage;
      if (this.isNewReport) return this.reportMessages.newReport;

      return {};
    },
    tableData() {
      return this.data && this.data.rows && !this.localLoading
        ? this.data.rows
        : [];
    },
    tableColumns() {
      const columns = [];

      if (!this.dimension || this.localLoading) return columns;

      // build dimension column
      if (this.dimension === this.allSegments.value) {

        columns.push({
          value: 'full_segment_name',
          name: this.allSegments.key,
        });

      } else {
        const dimensionObj = getReportFieldFromValue(this.allReportFields, this.dimension);

        columns.push({
          value: this.includeFullSegments ? 'full_segment_name' : dimensionObj.snake,
          name: dimensionObj.key,
        });
      }

      // build metrics columns
      for (const metric of this.metrics) {
        const reportField = getReportFieldFromValue(this.allReportFields, metric);

        columns.push({
          value: reportField.snake,
          name: getDemographicsFieldName(reportField),
        });
      }

      return columns;
    },
  },
  mounted() {
    this.setSortKey();
  },
  methods: {
    runAllSegments() {
      this.$emit('runAllSegments');
    },
    setSortKey() {
      // Default sort by page views OR first metric in report
      if (this.metrics?.length) {
        this.sortKey = this.metrics.includes('pageViews')
          ? 'page_views'
          : getReportFieldFromValue(this.allReportFields, this.metrics[0]).snake;
      }
    },
  },
  components: {
    FilterRow,
    ChartBlock,
    ReportMessage,
    Table,
  },
  watch: {
    metrics() {
      // Prevent loading of table body in the milliseconds between
      // metrics being updated and making the new fetch request
      this.localLoading = true;
      this.setSortKey();
    },
    loading(val) {
      this.localLoading = val;
    },
  },
};
</script>
<style lang="scss" scoped>
.actions-row {
  height: 56px;
  border-bottom: 1px solid #C1CCD4;
  display: flex;
}

.filter-row {
  padding-left: 16px;
}

.demographics-content {
  margin: 16px 0px 100px 0px;
}

// default vuetify z index is higher than slideout overlay
::v-deep
  .vuetify-styling
  .v-data-table--fixed-header>.v-data-table__wrapper>table>thead>tr>th {
  z-index: 1;
}
</style>
